// src/products/analysis/pages/PrivateCompaniesPage.js
import React, { useCallback, useMemo, useState } from 'react';
import {
  AlertCircle,
  Building,
  FileText,
  Loader2,
  MapPin,
  RefreshCw,
  Search,
  ShieldCheck
} from 'lucide-react';
import {
  getCompanyDetails,
  searchPrivateCompanies
} from '../services/secService';

const formatAddress = (address) => {
  if (!address) return '—';
  const parts = [
    address.street1,
    address.street2,
    [address.city, address.stateOrCountry].filter(Boolean).join(', '),
    address.zipCode
  ].filter(Boolean);
  return parts.length ? parts.join(', ') : '—';
};

const formVariants = {
  D: 'bg-amber-100 text-amber-800 border-amber-200',
  C: 'bg-blue-100 text-blue-800 border-blue-200',
  DEFAULT: 'bg-gray-100 text-gray-800 border-gray-200'
};

const highlightClass = (form) => {
  if (!form) return formVariants.DEFAULT;
  const key = form.startsWith('D') ? 'D' : form.startsWith('C') ? 'C' : 'DEFAULT';
  return formVariants[key] || formVariants.DEFAULT;
};

const additionalSources = [
  {
    name: 'OpenCorporates API',
    url: 'https://api.opencorporates.com/',
    description: 'Global company registry aggregation with 200M+ records. Free tier allows 250 calls/day.'
  },
  {
    name: 'UK Companies House',
    url: 'https://developer.company-information.service.gov.uk/',
    description: 'REST API for filings, officers, PSCs for UK private entities. Completely free with key.'
  },
  {
    name: 'SEC Company Facts',
    url: 'https://www.sec.gov/edgar/sec-api-documentation',
    description: 'XBRL facts API exposes revenue, headcount, and financial metrics for private filers that submit structured data.'
  },
  {
    name: 'Crunchbase Open Data Map',
    url: 'https://data.crunchbase.com/docs/open-data-map',
    description: 'Downloadable CSV snapshot of startup profiles, investors, and funding rounds (updated monthly).'
  },
  {
    name: 'AngelList GraphQL',
    url: 'https://angel.co/api',
    description: 'Public GraphQL endpoint for startup jobs, investors, and funding signals. Useful for cross-checking private firms.'
  }
];

const exampleQueries = [
  'Stripe Inc',
  'Space Exploration Technologies',
  'Anduril Industries',
  'OpenAI Global',
  'Impossible Foods'
];

const PrivateCompaniesPage = () => {
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);

  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [companyError, setCompanyError] = useState(null);
  const [companyData, setCompanyData] = useState(null);

  const handleSearch = useCallback(async () => {
    try {
      if (query.trim().length < 2) {
        setSearchError('Enter at least 2 characters.');
        return;
      }

      setSearchLoading(true);
      setSearchError(null);
      const results = await searchPrivateCompanies(query);
      setSearchResults(results);
      if (!results.length) {
        setSearchError('No SEC filer matches found. Try a different keyword.');
      }
    } catch (error) {
      setSearchError(error.message || 'Search failed.');
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, [query]);

  const handleSelectCompany = useCallback(async (company) => {
    setSelectedCompany(company);
    setCompanyError(null);
    setCompanyData(null);
    try {
      setCompanyLoading(true);
      const data = await getCompanyDetails(company.cik);
      setCompanyData(data);
    } catch (error) {
      setCompanyError(error.message || 'Failed to load company filings.');
    } finally {
      setCompanyLoading(false);
    }
  }, []);

  const regDSummary = useMemo(() => {
    if (!companyData?.regDFilings?.length) return null;
    const latest = companyData.regDFilings[0];
    return {
      count: companyData.regDFilings.length,
      latestForm: latest.form,
      latestDate: latest.filingDate
    };
  }, [companyData]);

  const onKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSearch();
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Private Company Filings</h1>
          <p className="text-sm text-gray-600 mt-1">
            Search SEC EDGAR submissions to surface Reg D, Reg CF and other private-company disclosures.
          </p>
        </div>
        <div className="hidden md:flex items-center gap-2 text-xs text-gray-500">
          <ShieldCheck className="w-4 h-4" />
          SEC data refreshed live via compliant EDGAR requests
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-5 space-y-4">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Search by company name, fund, or filing entity"
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            onClick={handleSearch}
            className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            {searchLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
          </button>
        </div>
        {searchError && (
          <div className="flex items-center gap-2 text-sm text-red-600">
            <AlertCircle className="w-4 h-4" />
            {searchError}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
          <div className="border border-dashed border-blue-200 rounded-lg p-4 bg-blue-50/40">
            <div className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-2">
              Try These Sample Searches
            </div>
            <div className="flex flex-wrap gap-2">
              {exampleQueries.map((sample) => (
                <button
                  key={sample}
                  type="button"
                  onClick={() => setQuery(sample)}
                  className="px-3 py-1 bg-white border border-blue-200 rounded-full text-xs text-blue-700 hover:bg-blue-100 transition"
                >
                  {sample}
                </button>
              ))}
            </div>
            <p className="mt-3 text-xs text-blue-600">
              Tip: many private issuers register subsidiaries under a slightly different legal name. If a search fails, remove commas or abbreviations (e.g. use “OpenAI Global” instead of “OpenAI Global LLC”).
            </p>
          </div>
          <div className="border border-dashed border-gray-200 rounded-lg p-4 bg-gray-50">
            <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
              How filings help
            </div>
            <ul className="list-disc list-inside space-y-1">
              <li>Reg D forms reveal raise amount, investor count, and exemption used.</li>
              <li>Primary documents often embed exec emails and onboarding decks—download from the table below.</li>
              <li>Cross-reference SIC and incorporation state to group comparable private peers.</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
              Search Results
            </h2>
            <span className="text-xs text-gray-500">{searchResults.length} matches</span>
          </div>
          <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1">
            {searchLoading && (
              <div className="flex items-center justify-center py-12 text-gray-500 text-sm">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Searching SEC registry…
              </div>
            )}
            {!searchLoading && !searchResults.length && (
              <div className="text-sm text-gray-500 py-4">
                Enter a keyword to discover private-company filings.
              </div>
            )}
            {searchResults.map((company) => {
              const isSelected = selectedCompany?.cik === company.cik;
              return (
                <button
                  key={`${company.cik}-${company.name}`}
                  onClick={() => handleSelectCompany(company)}
                  className={`w-full text-left border rounded-lg px-3 py-2 transition ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-400 hover:bg-blue-50/40'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Building className="w-4 h-4 text-blue-500" />
                    <span className="font-medium text-sm text-gray-900 line-clamp-2">
                      {company.name}
                    </span>
                  </div>
                  <div className="mt-1 text-xs text-gray-500 flex items-center gap-3">
                    <span>CIK: {company.cik}</span>
                    {company.entityType && <span>{company.entityType}</span>}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-5 min-h-[220px]">
            {companyLoading && (
              <div className="flex flex-col items-center justify-center py-16 text-gray-500">
                <Loader2 className="w-6 h-6 animate-spin mb-3" />
                Fetching SEC submissions…
              </div>
            )}

            {!companyLoading && companyError && (
              <div className="flex items-start gap-2 text-red-600 text-sm">
                <AlertCircle className="w-5 h-5 mt-0.5" />
                <div>
                  <p className="font-medium">Unable to load filing history</p>
                  <p>{companyError}</p>
                </div>
              </div>
            )}

            {!companyLoading && !companyError && companyData && (
              <div className="space-y-5">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">
                      {companyData.profile?.name || selectedCompany?.name}
                    </h2>
                    <div className="text-sm text-gray-600 mt-1 flex flex-wrap gap-3">
                      <span>CIK: {companyData.profile?.cik}</span>
                      {companyData.profile?.entityType && (
                        <span>{companyData.profile.entityType}</span>
                      )}
                      {companyData.profile?.sicDescription && (
                        <span>{companyData.profile.sicDescription}</span>
                      )}
                    </div>
                  </div>
                  {regDSummary && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
                      <div className="font-semibold">Reg D Activity</div>
                      <div>{regDSummary.count} filings, latest {regDSummary.latestForm} on {regDSummary.latestDate}</div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center gap-2 text-gray-700 font-medium">
                      <MapPin className="w-4 h-4" /> Business Address
                    </div>
                    <p className="mt-2 text-gray-600 whitespace-pre-line">
                      {formatAddress(companyData.profile?.businessAddress)}
                    </p>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center gap-2 text-gray-700 font-medium">
                      <MapPin className="w-4 h-4" /> Mailing Address
                    </div>
                    <p className="mt-2 text-gray-600 whitespace-pre-line">
                      {formatAddress(companyData.profile?.mailingAddress)}
                    </p>
                  </div>
                </div>

                {companyData.profile?.formerNames?.length > 0 && (
                  <div className="border border-gray-200 rounded-lg p-3">
                    <div className="text-sm font-medium text-gray-700">Former Names</div>
                    <ul className="mt-2 text-sm text-gray-600 space-y-1">
                      {companyData.profile.formerNames.map((entry, index) => (
                        <li key={`${entry.name}-${index}`}>
                          {entry.name} {entry.fromDate && `(from ${entry.fromDate})`}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {!companyLoading && !companyData && !companyError && (
              <div className="text-sm text-gray-500">
                Select a company to review recent private filings, download Reg D disclosures, and export contact information.
              </div>
            )}
          </div>

          <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                Recent SEC Filings
              </h3>
              <button
                onClick={() => selectedCompany && handleSelectCompany(selectedCompany)}
                disabled={!selectedCompany || companyLoading}
                className="inline-flex items-center gap-2 text-xs text-blue-600 hover:text-blue-800 disabled:text-gray-400"
              >
                <RefreshCw className={`w-3 h-3 ${companyLoading ? 'animate-spin' : ''}`} /> Refresh
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Form
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Filed
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Description
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Access
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {companyLoading && (
                    <tr>
                      <td colSpan={4} className="px-4 py-6 text-center text-gray-500">
                        <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading filings…
                      </td>
                    </tr>
                  )}
                  {!companyLoading && companyData?.recentFilings?.length ? (
                    companyData.recentFilings.slice(0, 25).map((filing) => (
                      <tr key={filing.accessionNumber}>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 rounded border text-xs font-semibold ${highlightClass(filing.form)}`}>
                            {filing.form || '—'}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                          {filing.filingDate || '—'}
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {filing.primaryDocDescription || filing.primaryDocument || '—'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {filing.url ? (
                            <a
                              href={filing.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800"
                            >
                              <FileText className="w-4 h-4" /> Document
                            </a>
                          ) : (
                            <span className="text-gray-400">N/A</span>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    !companyLoading && (
                      <tr>
                        <td colSpan={4} className="px-4 py-6 text-center text-sm text-gray-500">
                          No filings available. Try selecting a different company or refresh once more.
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">More Free Data Sources</h2>
            <p className="text-xs text-gray-500 mt-1">
              Layer these APIs with SEC filings to round out ownership, funding, and director detail.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
          {additionalSources.map((source) => (
            <div key={source.name} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition">
              <div className="text-sm font-semibold text-gray-900 mb-1">{source.name}</div>
              <p className="text-xs text-gray-600 mb-3">{source.description}</p>
              <a
                href={source.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800"
              >
                Explore API →
              </a>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PrivateCompaniesPage;


